import { useCallback, useEffect } from 'react';
import {
  useCreateQuestMutation,
  useUpdateQuestMutation,
  SeasonViewDocument,
} from '../../../../../../schema';
import { toast } from 'react-toastify';
import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { Quest, QuestType, QuestFormValues } from './types';

type Props = {
  seasonId: string;
  quest?: Quest | null;
  questType: QuestType;
  onClose: () => void;
};

export const useQuestForm = ({ seasonId, quest, questType, onClose }: Props) => {
  const isEdit = !!quest;

  const [createQuest, { data: createData, loading: createLoading }] = useCreateQuestMutation();
  const [updateQuest, { data: updateData, loading: updateLoading }] = useUpdateQuestMutation();

  const loading = createLoading || updateLoading;

  const getQuestSettings = (q: Quest | null | undefined) => {
    if (!q) return { city: '', gender: '' as const, minimumCount: '' };

    if (q.__typename === 'SubscribeToWardQuest' || q.__typename === 'OneTimeDonateToWardQuest') {
      return {
        city: q.city ?? '',
        gender: q.gender ?? ('' as const),
        minimumCount: q.minimumCount ?? '',
      };
    }
    return { city: '', gender: '' as const, minimumCount: '' };
  };

  const settings = getQuestSettings(quest);

  const initialValues: QuestFormValues = {
    title: quest?.title ?? '',
    description: quest?.description ?? '',
    rewardAmount: quest?.rewardAmount ?? '',
    city: settings.city,
    gender: settings.gender,
    minimumCount: settings.minimumCount ?? '',
  };

  const buildQuestInput = (values: QuestFormValues) => {
    const minimumCount = values.minimumCount
      ? typeof values.minimumCount === 'string'
        ? parseInt(values.minimumCount, 10)
        : values.minimumCount
      : undefined;

    const questSettings = {
      city: values.city || null,
      gender: values.gender || null,
      minimumCount: minimumCount || null,
    };

    if (questType === 'SubscribeToWardQuest') {
      return { subscribeToWard: questSettings };
    } else {
      return { oneTimeDonateToWard: questSettings };
    }
  };

  const onSubmit = useCallback(
    async (values: QuestFormValues) => {
      try {
        const questInput = buildQuestInput(values);
        const rewardAmount =
          typeof values.rewardAmount === 'string'
            ? parseInt(values.rewardAmount, 10)
            : values.rewardAmount;

        const description = values.description?.trim() || null;

        if (isEdit && quest) {
          await updateQuest({
            variables: {
              input: {
                questId: quest.id,
                title: values.title,
                description,
                rewardAmount,
                quest: questInput,
              },
            },
            refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
          });
        } else {
          await createQuest({
            variables: {
              input: {
                seasonId,
                title: values.title,
                description,
                rewardAmount,
                quest: questInput,
              },
            },
            refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
          });
        }
      } catch {
        toast.error(isEdit ? 'Ошибка сохранения задания' : 'Ошибка создания задания');
        return {
          [FORM_ERROR]: isEdit ? 'Ошибка сохранения' : 'Ошибка создания',
        } as SubmissionErrors;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, quest, questType, seasonId, createQuest, updateQuest]
  );

  useEffect(() => {
    const successData = createData?.createQuest.quest || updateData?.updateQuest.quest;

    if (successData) {
      toast.success(isEdit ? 'Задание успешно сохранено' : 'Задание успешно создано');
      onClose();
    }
  }, [createData, updateData, isEdit, onClose]);

  return { initialValues, onSubmit, loading, isEdit };
};
